
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useMutation } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";

interface CategoryFormData {
  name: string;
  description: string;
  parent_id?: string;
  image_url?: string;
}

interface CategoryFormProps {
  category?: any;
  categories: any[];
  onSaved: () => void;
  onCancel: () => void;
}

const CategoryForm = ({ category, categories, onSaved, onCancel }: CategoryFormProps) => {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<CategoryFormData>({
    defaultValues: {
      name: category?.name || "",
      description: category?.description || "",
      parent_id: category?.parent_id || "none",
      image_url: category?.image_url || "",
    },
  });

  const mutation = useMutation({
    mutationFn: async (data: CategoryFormData) => {
      const categoryData = {
        ...data,
        parent_id: data.parent_id === "none" ? null : data.parent_id,
      };

      if (category) {
        const { error } = await supabase
          .from('categories')
          .update(categoryData)
          .eq('id', category.id);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('categories')
          .insert([categoryData]);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      toast({
        title: "Success",
        description: `Category ${category ? 'updated' : 'created'} successfully`,
      });
      onSaved();
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: `Failed to ${category ? 'update' : 'create'} category`,
        variant: "destructive",
      });
    },
  });

  const onSubmit = (data: CategoryFormData) => {
    setIsSubmitting(true);
    mutation.mutate(data);
    setIsSubmitting(false);
  };

  // Filter out current category and its children to prevent circular references
  const availableParents = categories.filter(cat => 
    cat.id !== category?.id && 
    cat.parent_id !== category?.id
  );

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      <div className="space-y-4">
        <div>
          <Label htmlFor="name">Category Name *</Label>
          <Input
            id="name"
            {...register("name", { required: "Category name is required" })}
            className="mt-1"
          />
          {errors.name && (
            <p className="text-sm text-red-500 mt-1">{errors.name.message}</p>
          )}
        </div>

        <div>
          <Label htmlFor="description">Description</Label>
          <Textarea
            id="description"
            {...register("description")}
            className="mt-1"
            rows={3}
          />
        </div>

        <div>
          <Label htmlFor="parent_id">Parent Category</Label>
          <Select
            value={watch("parent_id")}
            onValueChange={(value) => setValue("parent_id", value)}
          >
            <SelectTrigger className="mt-1">
              <SelectValue placeholder="Select parent category (optional)" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">No Parent (Root Category)</SelectItem>
              {availableParents.map((cat) => (
                <SelectItem key={cat.id} value={cat.id}>
                  {cat.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label htmlFor="image_url">Image URL</Label>
          <Input
            id="image_url"
            {...register("image_url")}
            className="mt-1"
            placeholder="https://example.com/image.jpg"
          />
          {watch("image_url") && (
            <div className="mt-2">
              <img
                src={watch("image_url")}
                alt="Preview"
                className="w-32 h-32 object-cover rounded-lg border"
              />
            </div>
          )}
        </div>
      </div>

      <div className="flex items-center justify-end space-x-4 pt-6 border-t border-admin-border">
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          disabled={isSubmitting}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          disabled={isSubmitting}
          className="bg-admin-accent-blue hover:bg-admin-accent-blue/90"
        >
          {isSubmitting ? "Saving..." : category ? "Update Category" : "Create Category"}
        </Button>
      </div>
    </form>
  );
};

export default CategoryForm;
